import React, { PropsWithChildren } from 'react'

import clsx from 'clsx'

import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import ValuePropListItemIcon from '$extensionSrc/icons/value-prop-list-item-icon.svg'

import styles from './PlanOption.module.scss'

function BenefitListItem({ children }: PropsWithChildren<{}>) {
  return (
    <div className={styles.BenefitListItem}>
      <ValuePropListItemIcon className={styles.listIcon} aria-hidden />
      <div>{children}</div>
    </div>
  )
}

export default function PlanOption({
  benefitList,
  checked,
  discountBannerText,
  discountPrice,
  fullPrice,
  onClick,
  subtitle,
  title,
}: {
  benefitList: string[]
  checked: boolean | undefined
  discountBannerText: string | undefined
  discountPrice: number | undefined
  fullPrice: number | undefined
  onClick: () => void | undefined
  subtitle: string
  title: string
}) {
  const showInput = checked !== undefined && onClick !== undefined

  function formatPrice(price: number): string {
    if (price === null || price === undefined) {
      return ''
    }

    if (price === Math.ceil(price)) {
      return price.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
    }

    return price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return (
    <div className={clsx(styles.PlanOption)}>
      {discountBannerText && (
        <div className={styles.discountBanner}>{discountBannerText}</div>
      )}
      <InteractiveElement
        aria-label={`Select ${title}`}
        className={clsx(
          styles.card,
          showInput && styles.showInput,
          checked && styles.checked,
        )}
        onClick={onClick}
        tabIndex={showInput ? 0 : -1}
      >
        <div className={styles.topSection}>
          <div className={styles.topRow}>
            {showInput && (
              <div
                className={clsx(styles.radioInput, checked && styles.checked)}
              />
            )}
            <div className={styles.titleSection}>
              <div className={styles.title}>{title}</div>
              <div className={styles.price}>
                {fullPrice !== undefined && discountPrice !== undefined && (
                  <>
                    <del
                      className={styles.discountedFullPrice}
                      aria-label={`Original price: $${formatPrice(fullPrice)}`}
                    >
                      ${formatPrice(fullPrice)}
                    </del>{' '}
                    <ins
                      className={styles.newPrice}
                      aria-label={`Discounted price: $${formatPrice(discountPrice)}`}
                    >
                      ${formatPrice(discountPrice)}
                    </ins>
                  </>
                )}
                {fullPrice !== undefined && discountPrice === undefined && (
                  <span>${formatPrice(fullPrice)}</span>
                )}
              </div>
            </div>
          </div>
          <div className={clsx(styles.subtitle, showInput && styles.showInput)}>
            {subtitle}
          </div>
        </div>
        <div className={styles.benefitListSection}>
          <div>You'll get:</div>
          <div className={styles.bullets}>
            {benefitList.map((text) => (
              <BenefitListItem key={text}>{text}</BenefitListItem>
            ))}
          </div>
        </div>
      </InteractiveElement>
    </div>
  )
}
